import React from 'react';
import { StaticImage } from "gatsby-plugin-image";
import { Button } from "@/components/ui/button";
import { BOOKING_URL, LINK_ATTRIBUTES } from "@/lib/config";
import { Shield, CreditCard, Calendar, Building2 } from "lucide-react";
import BookingLeadForm from "@/components/BookingLeadForm";

const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 w-full h-full">
        <StaticImage
          src="../assets/clinic-interior-y3-99.jpg"
          alt="Y3 Smiles Dental Clinic Interior"
          className="w-full h-full"
          imgClassName="object-cover w-full h-full object-center"
          layout="fullWidth"
          placeholder="dominantColor"
          loading="eager"
          fetchpriority="high"
          formats={["avif", "webp"]}
          quality={30}
        />
        {/* Gradient Overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/80 to-white/40"></div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 relative z-10 py-24 lg:py-32">
        <div className="max-w-7xl mx-auto">
          {/* Desktop: 2-column layout (left content, right booking). Mobile: stacked with reordering */}
          <div className="grid lg:grid-cols-5 gap-8 animate-fade-in-up">
            {/* Left column: headline, cards, payments (order changed on mobile) */}
            <div className="lg:col-span-3 space-y-8 flex flex-col lg:justify-between lg:min-h-[800px]">
              {/* Main Headline - order-1 on mobile */}
              <div className="order-1 space-y-4 text-center lg:text-left">
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-800 leading-tight">
                  <span className="block">Family</span>
                  <span className="block text-primary">Dentist</span>
                  <span className="block">in Broadmeadows</span>
                </h1>

                {/* Subheadline */}
                <p className="text-lg text-neutral-800 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                  We help everyone in your family get healthy teeth. Same-day appointments for emergencies.
                  Free dental care for kids (CDBS). Easy payment plans available.
                </p>

                {/* Mobile CTA */}
                <div className="lg:hidden text-center">
                   <Button 
                    className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-white font-bold text-lg px-8 py-6 rounded-xl shadow-lg"
                    onClick={() => document.getElementById('booking-portal-frame')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  >
                    Book Online Now
                  </Button>
                </div>

                {/* Google Reviews Badge */}
                <a 
                  href="https://share.google/UtLnH37BZ0aKupvgP" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="inline-flex items-center gap-3 bg-white/80 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/50 shadow-lg hover:shadow-xl hover:bg-white/90 transition-all duration-300 group"
                >
                  <svg className="w-8 h-8 text-primary" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <div className="text-left">
                    <div className="text-yellow-500 text-lg font-bold leading-none mb-1">★★★★★</div>
                    <div className="text-xs text-neutral-700 font-semibold">Rated 5 stars by our patients</div>
                  </div>
                  <svg className="w-4 h-4 text-neutral-600 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </a>

                {/* Mobile — Dr Fatima headshot, no frame */}
                <div className="lg:hidden flex justify-center w-full">
                  <img
                    src="/DR%20Fatima%20Headshot%20New.png?v=20260421"
                    alt="Dr Fatima Kurnaz, Principal Dentist at Y3 Smiles Dental"
                    className="object-contain object-bottom w-auto"
                    style={{ height: '300px' }}
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                  />
                </div>

                {/* Desktop — Dr Fatima headshot, no frame */}
                <div className="hidden lg:flex justify-center w-full">
                  <img
                    src="/DR%20Fatima%20Headshot%20New.png?v=20260421"
                    alt="Dr Fatima Kurnaz, Principal Dentist at Y3 Smiles Dental"
                    className="object-contain object-bottom w-auto"
                    style={{ height: '416px' }}
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                  />
                </div>
              </div>

              {/* Key Highlights - 4 compact cards - order-2 on mobile */}
              <div className="order-2 grid grid-cols-2 gap-3">
                <div className="group bg-white/20 backdrop-blur-sm rounded-lg p-4 border border-white/30 shadow-md hover:shadow-lg hover:border-primary/50 hover:bg-white/30 transition-all duration-300 text-center">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mx-auto mb-2 group-hover:scale-110 transition-transform">
                    <Shield className="w-5 h-5 text-primary drop-shadow-lg" />
                  </div>
                  <p className="text-sm font-semibold text-neutral-800 group-hover:text-primary transition-colors drop-shadow-md">All Major Health Insurers</p>
                  <p className="text-xs text-neutral-700 drop-shadow">Accepted</p>
                </div>
                <div className="group bg-white/20 backdrop-blur-sm rounded-lg p-4 border border-white/30 shadow-md hover:shadow-lg hover:border-primary/50 hover:bg-white/30 transition-all duration-300 text-center">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mx-auto mb-2 group-hover:scale-110 transition-transform">
                    <CreditCard className="w-5 h-5 text-primary drop-shadow-lg" />
                  </div>
                  <p className="text-sm font-semibold text-neutral-800 group-hover:text-primary transition-colors drop-shadow-md">Free Kids Dental</p>
                  <p className="text-xs text-neutral-700 drop-shadow">CDBS Accepted*</p>
                  <p className="text-[10px] text-neutral-600 drop-shadow mt-1">*Private practice</p>
                </div>
                <div className="group bg-white/20 backdrop-blur-sm rounded-lg p-4 border border-white/30 shadow-md hover:shadow-lg hover:border-primary/50 hover:bg-white/30 transition-all duration-300 text-center">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mx-auto mb-2 group-hover:scale-110 transition-transform">
                    <Calendar className="w-5 h-5 text-primary drop-shadow-lg" />
                  </div>
                  <p className="text-sm font-semibold text-neutral-800 group-hover:text-primary transition-colors drop-shadow-md">Payment Plans</p>
                  <p className="text-xs text-neutral-700 drop-shadow">Available</p>
                </div>
                <div className="group bg-white/20 backdrop-blur-sm rounded-lg p-4 border border-white/30 shadow-md hover:shadow-lg hover:border-primary/50 hover:bg-white/30 transition-all duration-300 text-center">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mx-auto mb-2 group-hover:scale-110 transition-transform">
                    <Building2 className="w-5 h-5 text-primary drop-shadow-lg" />
                  </div>
                  <p className="text-sm font-semibold text-neutral-800 group-hover:text-primary transition-colors drop-shadow-md">Dental Clinic</p>
                  <p className="text-xs text-neutral-700 drop-shadow">Since 2025</p>
                </div>
              </div>
            </div>

            {/* Right column: Embedded Booking Form - order-3 on mobile */}
            <div className="lg:col-span-2 order-3 lg:order-4">
              <div id="booking-portal-frame" className="bg-white/90 backdrop-blur-md rounded-2xl border border-white/50 shadow-xl p-6 flex flex-col lg:sticky lg:top-24">
                {/* Intro copy */}
                <div className="flex flex-col justify-center mb-4 text-center lg:text-left">
                  <h3 className="text-xl font-bold text-neutral-800 mb-1">Request an Appointment</h3>
                  <p className="text-sm text-neutral-600">Enter your details and we'll send you a secure booking link instantly.</p>
                </div>
                {/* Booking Form */}
                <div>
                  <BookingLeadForm />
                </div>
                <div className="text-center mt-4 text-xs text-neutral-500">
                  By submitting, you agree to our <a href="/privacy-policy" className="text-primary underline hover:text-primary/80">Privacy Policy</a>.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
